import React from 'react';
import { Box, Typography, Chip, Link } from '@mui/material';

/**
 * Where a stylist has got to on their way to an in-person appointment.
 *
 * An OpenStreetMap embed rather than a map library: it needs no API key and no
 * new dependency, and the whole panel only exists while somebody is actually
 * driving, which is rare enough that a full interactive map would be weight
 * carried for nothing. The iframe src changes as the position does, so it
 * follows the stylist.
 */
export default function BookingEnRouteMap({ position, brand, ink }) {
  if (!position || position.latitude == null || position.longitude == null) return null;

  const { latitude, longitude, state, updated_at: updatedAt } = position;
  const arrived = state === 'arrived';

  // A box roughly a kilometre across, centred on the stylist.
  const d = 0.006;
  const bbox = [longitude - d, latitude - d / 2, longitude + d, latitude + d / 2].join('%2C');
  const src =
    `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}` +
    `&layer=mapnik&marker=${latitude}%2C${longitude}`;

  const seenAt = updatedAt ? new Date(updatedAt) : null;
  const secondsAgo = seenAt ? Math.round((Date.now() - seenAt.getTime()) / 1000) : null;
  const freshness =
    secondsAgo == null
      ? null
      : secondsAgo < 90
      ? 'just now'
      : secondsAgo < 3600
      ? `${Math.round(secondsAgo / 60)} min ago`
      : `${Math.round(secondsAgo / 3600)} hr ago`;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={arrived ? 'Stylist arrived' : 'Stylist en route'}
          color={arrived ? 'success' : 'warning'}
          variant="outlined"
        />
        {freshness && (
          <Typography variant="caption" sx={{ color: ink?.[500] }}>
            Last seen {freshness}
          </Typography>
        )}
        <Link
          href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ color: brand?.goldDeep }}
        >
          Open larger map
        </Link>
      </Box>
      <Box
        component="iframe"
        title="Stylist location"
        src={src}
        sx={{
          width: '100%',
          height: 220,
          border: 0,
          borderRadius: 2,
          display: 'block',
        }}
      />
    </Box>
  );
}
